import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeeApi } from '../../api/employeeApi';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  CurrencyRupeeIcon,
  IdentificationIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';

const EmployeeLedger = () => {
  const { activeBusiness } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Date context for calendar
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Transaction Modal State
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionType, setTransactionType] = useState('advance');
  const [transactionAmount, setTransactionAmount] = useState('');
  const [transactionDesc, setTransactionDesc] = useState('');
  const [calcData, setCalcData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id, currentMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch Employee Details
      const empRes = await employeeApi.getEmployees();
      const empData = empRes.data.data.find(e => e._id === id);
      setEmployee(empData);

      // Fetch Attendance for month (assuming the API returns all, we can filter)
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).toISOString();
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).toISOString();
      
      // Note: we might not have a getAttendance API on frontend yet, let's use a dummy array if it fails
      let attData = [];
      try {
        const attRes = await employeeApi.getAttendance(id, { startDate, endDate });
        attData = attRes.data.data || [];
      } catch (e) {
        console.warn('Attendance API not found, using empty data');
      }
      setAttendance(attData);

      // Fetch Transactions
      try {
        const transRes = await employeeApi.getTransactions(id);
        setTransactions(transRes.data.data || []);
      } catch (e) {
        console.warn('Failed to load transactions');
      }
      
      
    } catch (error) {
      console.error(error);
      toast.error('Failed to load ledger data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAttendance = async (date, status) => {
    try {
      await employeeApi.markAttendance(id, { date: date.toISOString(), status });
      toast.success(`Marked as ${status}`);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Failed to mark attendance');
    }
  };

  const handleDownloadIDCard = async () => {
    const loadingToast = toast.loading('Generating ID Card...');
    try {
      const res = await employeeApi.downloadIDCard(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ID_Card_${employee.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('ID Card downloaded!');
    } catch (error) {
      toast.error('Failed to generate ID Card');
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    if(!transactionAmount) return;
    
    try {
      const payload = {
        type: transactionType,
        amount: Number(transactionAmount),
        description: transactionDesc || 'Staff Entry'
      };

      if (transactionType === 'salary_payout' && calcData) {
        payload.earnedAmount = calcData.earnedSalary;
      }

      await employeeApi.addTransaction(id, payload);
      toast.success('Transaction added successfully');
      setShowTransactionModal(false);
      setTransactionAmount('');
      setTransactionDesc('');
      fetchData(); // Refresh to update balance and list
    } catch (error) {
      toast.error('Failed to add transaction');
    }
  };

  const openSalaryModal = () => {
    if (!employee) return;
    
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    let p = 0, a = 0, h = 0;
    
    const currentMonthAttendance = attendance.filter(record => {
      const d = new Date(record.date);
      return d.getMonth() === currentMonth.getMonth() && d.getFullYear() === currentMonth.getFullYear();
    });

    currentMonthAttendance.forEach(record => {
      if(record.status === 'present') p++;
      else if(record.status === 'absent') a++;
      else if(record.status === 'half-day') h++;
    });

    const allowedLeaves = activeBusiness?.settings?.allowedPaidLeavesPerMonth || 2;
    let paidLeavesUsed = Math.min(a, allowedLeaves);
    let effectiveAbsent = Math.max(0, a - allowedLeaves);

    let earned = 0;
    if (employee.salaryType === 'monthly') {
      const perDaySalary = employee.salaryAmount / daysInMonth;
      earned = perDaySalary * (p + (0.5 * h) + paidLeavesUsed);
    } else {
      earned = employee.salaryAmount * (p + (0.5 * h) + paidLeavesUsed);
    }
    
    earned = Math.round(earned);

    setCalcData({
      present: p,
      absent: a,
      paidLeavesUsed: paidLeavesUsed,
      effectiveAbsent: effectiveAbsent,
      halfDays: h,
      totalDays: daysInMonth,
      earnedSalary: earned,
      previousBalance: employee.currentBalance || 0,
      netPayable: Math.max(0, earned + (employee.currentBalance || 0))
    });
    
    const netPayable = Math.max(0, earned + (employee.currentBalance || 0));
    const balanceText = employee.currentBalance < 0 
      ? ` (Advance Recovery: ${Math.abs(employee.currentBalance)})` 
      : (employee.currentBalance > 0 ? ` (Previous Due: ${employee.currentBalance})` : '');

    setTransactionType('salary_payout');
    setTransactionAmount(netPayable.toString());
    setTransactionDesc(`Salary for ${currentMonth.toLocaleString('default', { month: 'short', year: 'numeric' })} (Earned: ${earned})${balanceText}`);
    setShowTransactionModal(true);
  };

  // Calendar Helper functions
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const calendarDays = Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i + 1);
    return d;
  });

  const getAttendanceStatus = (date) => {
    const record = attendance.find(a => new Date(a.date).toDateString() === date.toDateString());
    return record ? record.status : null;
  };

  if (loading && !employee) {
    return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#dc343b]"></div></div>;
  }

  return (
    <div className="w-full pb-20 max-w-7xl mx-auto space-y-6 pt-2">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/employees')}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              {employee?.name}'s Ledger
            </h1>
            <p className="text-[14px] text-gray-500">{employee?.role} • {employee?.salaryType === 'monthly' ? 'Monthly Salary' : 'Daily Wage'}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleDownloadIDCard}
            className="flex items-center gap-2 bg-white text-[#dc343b] border border-gray-200 px-4 py-2.5 rounded-xl font-bold text-[13px] hover:bg-red-50 transition-colors shadow-sm"
          >
            <IdentificationIcon className="w-4 h-4" />
            Print ID Card
          </button>
          <button 
            onClick={openSalaryModal}
            className="flex items-center gap-2 bg-green-600 text-white px-5 py-2.5 rounded-xl font-bold text-[13px] hover:bg-green-700 transition-colors shadow-sm shadow-green-500/20"
          >
            <CurrencyRupeeIcon className="w-4 h-4" />
            Pay Salary
          </button>
          <button 
            onClick={() => { setTransactionType('advance'); setShowTransactionModal(true); }}
            className="flex items-center gap-2 bg-[#dc343b] text-white px-5 py-2.5 rounded-xl font-bold text-[13px] hover:bg-[#b91c1c] transition-colors shadow-sm shadow-red-500/20"
          >
            <CurrencyRupeeIcon className="w-4 h-4" />
            Give Advance
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Attendance Calendar */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] border border-gray-100 dark:border-gray-700">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <CalendarDaysIcon className="w-6 h-6 text-[#dc343b]" />
              Attendance ({currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })})
            </h2>
            <div className="flex gap-2">
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="px-3 py-1 bg-gray-100 rounded-lg text-sm font-bold">&lt;</button>
              <button onClick={() => setCurrentMonth(new Date())} className="px-3 py-1 bg-gray-100 rounded-lg text-sm font-bold">Today</button>
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="px-3 py-1 bg-gray-100 rounded-lg text-sm font-bold">&gt;</button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center font-bold text-xs text-gray-400 py-2">{day}</div>
            ))}
            
            {/* Blank spaces for first day offset */}
            {Array.from({ length: new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay() }).map((_, i) => (
              <div key={`empty-${i}`} className="p-2"></div>
            ))}

            {calendarDays.map((date) => {
              const status = getAttendanceStatus(date);
              const isToday = date.toDateString() === new Date().toDateString();
              
              let bgColor = 'bg-gray-50 hover:bg-gray-100';
              if (status === 'present') bgColor = 'bg-green-100 text-green-800 border border-green-200';
              if (status === 'absent') bgColor = 'bg-red-100 text-red-800 border border-red-200';
              if (status === 'half-day') bgColor = 'bg-yellow-100 text-yellow-800 border border-yellow-200';
              
              return (
                <div key={date.toString()} className={`relative p-2 rounded-xl h-20 border border-transparent flex flex-col justify-between ${bgColor} group transition-colors`}>
                  <span className={`text-sm font-bold ${isToday ? 'text-[#dc343b] underline' : ''}`}>{date.getDate()}</span>
                  
                  {/* Status abbreviation */}
                  {status && (
                    <span className="text-xs font-extrabold text-center uppercase">
                      {status === 'half-day' ? 'H/D' : status[0]}
                    </span>
                  )}
                  
                  {/* Quick Action Overlay on Hover */}
                  <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-xl opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center gap-1 transition-opacity z-10 border shadow-sm cursor-pointer">
                    <button onClick={() => handleMarkAttendance(date, 'present')} className="text-[10px] w-full px-1 py-1 font-bold text-green-600 hover:bg-green-50 rounded">P</button>
                    <button onClick={() => handleMarkAttendance(date, 'half-day')} className="text-[10px] w-full px-1 py-1 font-bold text-yellow-600 hover:bg-yellow-50 rounded">H</button>
                    <button onClick={() => handleMarkAttendance(date, 'absent')} className="text-[10px] w-full px-1 py-1 font-bold text-red-600 hover:bg-red-50 rounded">A</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Ledger & Balances */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] border border-gray-100 dark:border-gray-700">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">Financial Overview</h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-2xl flex justify-between items-center border border-gray-100">
                <span className="text-sm font-bold text-gray-600">Base Salary</span>
                <span className="text-lg font-extrabold">₹{employee?.salaryAmount?.toLocaleString()}</span>
              </div>
              <div className={`p-4 rounded-2xl flex justify-between items-center border ${employee?.currentBalance >= 0 ? 'bg-red-50 border-red-100 text-red-700' : 'bg-green-50 border-green-100 text-green-700'}`}>
                <span className="text-sm font-bold">Net Balance</span>
                <span className="text-2xl font-extrabold">₹{Math.abs(employee?.currentBalance || 0).toLocaleString()}</span>
              </div>
              <p className="text-xs text-gray-400 text-center font-medium mt-2">
                {employee?.currentBalance > 0 
                  ? 'Employee owes you (Advance given)' 
                  : employee?.currentBalance < 0 
                    ? 'You have to pay (Salary pending)'
                    : 'Clear Balance'}
              </p>
            </div>
          </div>

          {/* Transaction Passbook */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] border border-gray-100 dark:border-gray-700">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">Passbook</h3>
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              {transactions.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No transactions yet.</p>
              ) : (
                transactions.map((tx) => (
                  <div key={tx._id} className="flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors">
                    <div>
                      <p className="text-[13px] font-bold text-gray-800">{tx.type.replace('_', ' ').toUpperCase()}</p>
                      <p className="text-[11px] font-bold text-gray-400 mt-0.5">{new Date(tx.date).toLocaleDateString('en-GB')} • {tx.description}</p>
                    </div>
                    <div className={`font-extrabold text-sm ${['advance', 'deduction'].includes(tx.type) ? 'text-red-500' : 'text-green-500'}`}>
                      {['advance', 'deduction'].includes(tx.type) ? '-' : '+'}₹{tx.amount}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Modal */}
      {showTransactionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Transaction</h2>
              <button onClick={() => setShowTransactionModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleAddTransaction} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Transaction Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setTransactionType('advance')} className={`px-4 py-3 rounded-xl font-bold text-sm border-2 transition-all ${transactionType === 'advance' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-100 text-gray-500 hover:bg-gray-50'}`}>Give Advance</button>
                  <button type="button" onClick={() => setTransactionType('salary_payout')} className={`px-4 py-3 rounded-xl font-bold text-sm border-2 transition-all ${transactionType === 'salary_payout' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-100 text-gray-500 hover:bg-gray-50'}`}>Pay Salary</button>
                  <button type="button" onClick={() => setTransactionType('bonus')} className={`px-4 py-3 rounded-xl font-bold text-sm border-2 transition-all ${transactionType === 'bonus' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-100 text-gray-500 hover:bg-gray-50'}`}>Add Bonus</button>
                  <button type="button" onClick={() => setTransactionType('deduction')} className={`px-4 py-3 rounded-xl font-bold text-sm border-2 transition-all ${transactionType === 'deduction' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-100 text-gray-500 hover:bg-gray-50'}`}>Deduction</button>
                </div>
              </div>

              {transactionType === 'salary_payout' && calcData && (
                <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-2xl mb-4">
                  <h4 className="text-xs font-bold text-indigo-800 uppercase tracking-wider mb-3">Auto-Calculated Summary</h4>
                  <div className="grid grid-cols-3 gap-2 text-center mb-3">
                    <div className="bg-white p-2 rounded-xl border border-indigo-50">
                      <p className="text-[10px] font-bold text-gray-400">Present</p>
                      <p className="text-sm font-extrabold text-green-600">{calcData.present}</p>
                    </div>
                    <div className="bg-white p-2 rounded-xl border border-indigo-50">
                      <p className="text-[10px] font-bold text-gray-400">Absent</p>
                      <p className="text-sm font-extrabold text-red-600">{calcData.absent}</p>
                    </div>
                    <div className="bg-white p-2 rounded-xl border border-indigo-50">
                      <p className="text-[10px] font-bold text-gray-400">Half Days</p>
                      <p className="text-sm font-extrabold text-yellow-600">{calcData.halfDays}</p>
                    </div>
                  </div>
                  
                  {calcData.paidLeavesUsed > 0 && (
                    <div className="flex justify-between items-center text-sm mb-2 bg-green-50 p-2 rounded-lg border border-green-100">
                      <span className="text-green-700 font-bold flex items-center gap-1">
                        <CheckCircleIcon className="w-4 h-4"/> Paid Leaves Applied:
                      </span>
                      <span className="font-bold text-green-700">+{calcData.paidLeavesUsed} Days</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center text-sm mb-2">
                    <span className="font-bold text-gray-600">Earned Salary:</span>
                    <span className="font-bold text-gray-600">₹{calcData.earnedSalary.toLocaleString()}</span>
                  </div>

                  {calcData.previousBalance !== 0 && (
                    <div className="flex justify-between items-center text-sm mb-2">
                      <span className="font-bold text-gray-600">
                        {calcData.previousBalance < 0 ? 'Advance Recovery:' : 'Previous Due (+):'}
                      </span>
                      <span className={`font-bold ${calcData.previousBalance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {calcData.previousBalance < 0 ? '-' : ''}₹{Math.abs(calcData.previousBalance).toLocaleString()}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center text-base pt-2 border-t border-indigo-100">
                    <span className="font-extrabold text-indigo-900">Net Payable:</span>
                    <span className="font-extrabold text-indigo-700">₹{calcData.netPayable.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Amount (₹)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">₹</span>
                  <input 
                    type="number" 
                    required 
                    value={transactionAmount}
                    onChange={(e) => setTransactionAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 rounded-xl border-2 border-gray-100 focus:border-[#dc343b] focus:ring-0 transition-colors font-bold text-gray-900" 
                    placeholder="Enter amount"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Description / Note</label>
                <input 
                  type="text" 
                  value={transactionDesc}
                  onChange={(e) => setTransactionDesc(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 focus:border-[#dc343b] focus:ring-0 transition-colors text-sm font-medium" 
                  placeholder="e.g. Diwali Bonus, Medical Advance"
                />
              </div>

              <div className="pt-2">
                <button type="submit" className="w-full bg-[#dc343b] hover:bg-[#b91c1c] text-white py-3.5 rounded-xl font-bold text-sm transition-colors shadow-lg shadow-red-500/20">
                  Save Transaction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeLedger;
